<template>
  <div v-if="hasErrors" class="error-display">
    <!-- Global Form Errors -->
    <div v-if="globalError" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-start">
        <svg class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <div>
          <h3 class="text-sm font-medium text-red-800">Form Submission Error</h3>
          <p class="text-sm text-red-700 mt-1">{{ globalError }}</p>
        </div>
      </div>
    </div>

    <!-- Validation Errors Summary -->
    <div v-if="validationErrors.length > 0" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-start">
        <svg class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <div class="flex-1">
          <h3 class="text-sm font-medium text-red-800">Please fix the following errors:</h3>
          <ul class="text-sm text-red-700 mt-2 space-y-1">
            <li v-for="error in validationErrors" :key="error.field" class="flex items-start">
              <span class="inline-block w-2 h-2 bg-red-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
              <span>{{ error.message }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Field-specific Errors -->
    <div v-if="fieldError" class="mb-2">
      <p class="text-sm text-red-600 flex items-center">
        <svg class="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ fieldError }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ValidationError, ValidationErrorLike } from '../types';

interface Props {
  errors?: ValidationErrorLike[];
  globalError?: string | null;
  fieldName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => [],
  globalError: null,
  fieldName: ''
});

const validationErrors = computed<ValidationError[]>(() => {
  return (props.errors || []).map((error, index) => {
    if (typeof error === 'string') {
      return {
        field: props.fieldName || `message_${index}`,
        message: error,
      };
    }

    return error;
  });
});

const fieldError = computed(() => {
  if (!props.fieldName) return null;
  
  const fieldErrors = validationErrors.value.filter(error => 
    error.field === props.fieldName || error.field.startsWith(`${props.fieldName}.`)
  );
  
  return fieldErrors.length > 0 ? fieldErrors[0].message : null;
});

const hasErrors = computed(() => {
  return !!(props.globalError || validationErrors.value.length > 0 || fieldError.value);
});
</script>

<style scoped>
@reference "../styles.css";

.error-display {
  @apply space-y-2;
}
</style>
